import { useState } from 'react'
import { useChangeMainCode } from 'src/hooks/useAffiliate'
import { AffiliateMe } from 'src/types/Affiliate'
import { formatBps } from 'src/utils/affiliateFormat'
import { copyText } from 'src/utils/clipboard'
import { notify } from 'src/utils/notify'
import { errorMessage } from './labels'
import { ActionButton, Card, CardTitle, ErrorText, Input, Label, Muted, Row, SecondaryButton } from './ui'

const CODE_PATTERN = /^[a-z0-9-]{3,20}$/

const copy = async (text: string) => {
    try {
        await copyText(text)
        notify('Copied to clipboard', 'success')
    } catch {
        notify("Couldn't copy. Select the text and copy it manually.", 'error')
    }
}

/** Append ?ref=code to a pasted dashboard URL, keeping its other params. Null if it isn't one of ours. */
const withRef = (raw: string, code: string) => {
    try {
        const url = new URL(raw.trim(), window.location.origin)
        const ours =
            url.hostname === window.location.hostname ||
            url.hostname === 'swiftsole.app' ||
            url.hostname.endsWith('.swiftsole.app')
        if (!ours || !/^https?:$/.test(url.protocol)) return null
        url.searchParams.set('ref', code)
        return url.toString()
    } catch {
        return null
    }
}

const ShareCard = ({ me }: { me: AffiliateMe }) => {
    const code = me.code || ''
    const [dropUrl, setDropUrl] = useState('')
    const [editing, setEditing] = useState(false)
    const [newCode, setNewCode] = useState(code)
    const updateCode = useChangeMainCode()

    const dropLink = dropUrl ? withRef(dropUrl, code) : null
    const normalized = newCode.trim().toLowerCase()
    const codeValid = CODE_PATTERN.test(normalized)
    const canEdit = me.status === 'active' && !me.terms?.needsAcceptance

    const saveCode = () =>
        updateCode.mutate(normalized, {
            onSuccess: () => {
                setEditing(false)
                notify('Code changed. Links with your old code still work.', 'success')
            },
        })

    return (
        <Card>
            <CardTitle>Share your link</CardTitle>
            <Label>
                Your personal link
                <Row $wrap={false}>
                    <Input readOnly value={me.shareUrl || ''} onFocus={(e) => e.target.select()} />
                    <SecondaryButton onClick={() => copy(me.shareUrl || '')}>Copy</SecondaryButton>
                </Row>
            </Label>

            <Label>
                Link to a specific drop
                <Input
                    placeholder="Paste a dashboard or release link"
                    value={dropUrl}
                    onChange={(e) => setDropUrl(e.target.value)}
                />
            </Label>
            {dropUrl &&
                (dropLink ? (
                    <Row $wrap={false}>
                        <Input readOnly value={dropLink} onFocus={(e) => e.target.select()} />
                        <SecondaryButton onClick={() => copy(dropLink)}>Copy</SecondaryButton>
                    </Row>
                ) : (
                    <ErrorText>That doesn't look like a SwiftSole dashboard link.</ErrorText>
                ))}

            {editing ? (
                <>
                    <Label>
                        New code
                        <Input
                            value={newCode}
                            maxLength={20}
                            onChange={(e) => setNewCode(e.target.value.toLowerCase())}
                            aria-invalid={!codeValid}
                        />
                    </Label>
                    <Muted>
                        3–20 characters: lowercase letters, numbers and dashes. Links you've already shared with your
                        current code keep working.
                    </Muted>
                    {updateCode.isError && <ErrorText>{errorMessage(updateCode.error)}</ErrorText>}
                    <Row>
                        <ActionButton
                            disabled={!codeValid || normalized === code || updateCode.isPending}
                            onClick={saveCode}
                        >
                            Change code
                        </ActionButton>
                        <SecondaryButton
                            onClick={() => {
                                setEditing(false)
                                setNewCode(code)
                                updateCode.reset()
                            }}
                        >
                            Cancel
                        </SecondaryButton>
                    </Row>
                </>
            ) : (
                <Row>
                    <Muted>
                        Your code: <strong style={{ color: 'white' }}>{code}</strong>
                    </Muted>
                    {canEdit && (
                        <SecondaryButton onClick={() => setEditing(true)} style={{ minHeight: 34 }}>
                            Change code
                        </SecondaryButton>
                    )}
                </Row>
            )}

            <Muted>
                You earn {formatBps(me.rates?.firstMonthBps)} of each referral's first month and{' '}
                {formatBps(me.rates?.recurringBps)} of every month after, for as long as they stay subscribed.{' '}
                <a href="/affiliate/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#8fa0ff' }}>
                    Affiliate terms
                </a>
            </Muted>
        </Card>
    )
}

export default ShareCard
